// Gate orchestrator for SAFE TSL blocks. Because a TSL block is a bounded declarative AST (not host code),
// there is no isolate and no static-token scan to do — `validateTslBlock` replaces scan+metadata, and the
// EXISTING determinism / no-lookahead / sizing / risk gates run via the interpreter runtime. Derives the
// same three tiers. Pure + deterministic; safe to run server-side in-edge.

import { TSL_DSL_VERSION, type TslBlock } from './ast';
import { validateTslBlock } from './validate';
import { createTslRuntime } from './interpret';
import { compatibilityGate, determinismGate, riskLifecycleGate, riskOptimizerGate, sizingBoundsGate } from '../blockGate/gates';
import type { BlockSubmission, GateReport, GateResult } from '../../types/customBlockLibrary';

const PER_BAR = new Set(['indicator', 'filter', 'analysis', 'signal_generator', 'pattern']);

export async function runTslGate(block: TslBlock): Promise<GateReport> {
  const results: GateResult[] = [];
  // 1. structure/type/budget validation replaces the JS scanner + metadata gate.
  results.push({ gate: 'static_scan', passed: true, detail: 'declarative TSL AST — no host code is representable' });
  const v = validateTslBlock(block);
  const meta: GateResult = { gate: 'metadata', passed: v.ok, detail: v.ok ? `${v.nodes} nodes · lookback ${v.lookback}` : v.errors.join('; ') };
  results.push(meta);

  const fail = (): GateReport => ({ scannerVersion: TSL_DSL_VERSION, publicVisible: { passed: false, results }, aiSafe: { passed: false, results }, optimizerSafe: { passed: false, results } });
  if (!v.ok) return fail();

  // 2. dynamic gates via the interpreter (deterministic, causal — no isolate).
  const rt = createTslRuntime(block);
  const sub: BlockSubmission = { category: block.category, blockType: block.blockType, name: 'tsl', description: 'tsl block', tags: [], code: '', paramSchema: block.params };

  let exec: GateResult; let categoryBaseline: GateResult | null = null;
  if (block.category === 'sizing') { categoryBaseline = await sizingBoundsGate(sub, rt); exec = categoryBaseline; }
  else if (block.category === 'risk_rules') { categoryBaseline = await riskLifecycleGate(sub, rt); exec = categoryBaseline; }
  else { exec = await determinismGate(sub, rt); }
  results.push(exec);
  const compat = await compatibilityGate(sub, rt); results.push(compat);

  const publicPass = exec.passed && compat.passed && (categoryBaseline ? categoryBaseline.passed : true);

  // ai_safe — stricter fuzzed determinism for per-bar blocks.
  const aiResults = [...results];
  let aiPass = publicPass;
  if (publicPass && PER_BAR.has(block.blockType)) { const strict = await determinismGate(sub, rt, [3, 4, 5, 6, 7]); aiResults.push(strict); aiPass = strict.passed; }

  // optimizer_safe — all params bounded; risk_rules need the stricter risk-optimizer gate.
  const allBounded = Object.values(block.params ?? {}).every((s) => s.kind === 'bool' || (s.kind === 'enum' && Array.isArray(s.values)) || (typeof s.min === 'number' && typeof s.max === 'number'));
  const optResults = [...aiResults, { gate: 'metadata', passed: allBounded, detail: allBounded ? 'all params bounded' : 'unbounded param(s)' } as GateResult];
  let riskOptOk = true;
  if (block.category === 'risk_rules') { if (aiPass && allBounded) { const rg = await riskOptimizerGate(sub, rt); riskOptOk = rg.passed; optResults.push(rg); } else riskOptOk = false; }
  const optPass = aiPass && allBounded && riskOptOk;

  return {
    scannerVersion: TSL_DSL_VERSION,
    publicVisible: { passed: publicPass, results },
    aiSafe: { passed: aiPass, results: aiResults },
    optimizerSafe: { passed: optPass, results: optResults },
  };
}
